import React, { useState, useRef, useEffect } from 'react';
import { fileRequester } from 'App';
import HLSPlayer from 'routes/video/HLSPlayer';

const VideoUploader = ({
    onUploadStart,
    onUploadComplete,
    basePath = '' // 기본값은 빈 문자열로 설정
}) => {
    // 상태 관리
    const [file, setFile] = useState(null);
    const [fileName, setFileName] = useState("선택된 파일 없음");
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [videoId, setVideoId] = useState(null);
    const [processingStatus, setProcessingStatus] = useState(null);

    // refs
    const fileInputRef = useRef(null);
    const dropAreaRef = useRef(null);
    const statusCheckInterval = useRef(null);

    // 파일 선택 핸들러
    const handleFileSelect = (e) => {
        const files = e.target.files || e.dataTransfer.files;
        if (files && files.length > 0) {
            handleFile(files[0]);
        }
    };

    // 파일 처리 함수
    const handleFile = (selectedFile) => {
        if (selectedFile && selectedFile.type.startsWith('video/')) {
            setFile(selectedFile);
            setFileName(selectedFile.name);

            // 미리보기 URL 생성 (로컬 파일)
            const objectUrl = URL.createObjectURL(selectedFile);
            setPreviewUrl(objectUrl);

            // 미리보기 URL은 메모리 누수 방지를 위해 컴포넌트 언마운트 시 해제해야 함
            return () => URL.revokeObjectURL(objectUrl);
        } else {
            alert('비디오 파일만 업로드 가능합니다.');
        }
    };

    // 드래그 영역 이벤트 핸들러
    const handleDragOver = (e) => {
        e.preventDefault();
        dropAreaRef.current.style.border = '2px dashed #4285f4';
    };

    const handleDragLeave = () => {
        dropAreaRef.current.style.border = '2px dashed #ccc';
    };

    const handleDrop = (e) => {
        e.preventDefault();
        dropAreaRef.current.style.border = '2px dashed #ccc';
        handleFileSelect(e);
    };

    // 업로드 핸들러
    const handleUpload = async () => {
        if (!file) {
            alert('업로드할 파일을 선택해주세요.');
            return;
        }

        setIsUploading(true);
        setUploadProgress(0);
        if (onUploadStart) onUploadStart();

        try {
            // FormData 생성
            const formData = new FormData();
            formData.append('file', file);

            // 저장 경로 설정 (연/월 기준으로 구성)
            const now = new Date();
            // basePath가 제공되면 해당 경로 사용, 아니면 연/월만 사용
            const path = basePath
                ? `${basePath}/${now.getFullYear()}/${now.getMonth() + 1}`
                : `${now.getFullYear()}/${now.getMonth() + 1}`;

            formData.append('path', path);

            // 비디오 업로드 전용 메소드 사용
            const response = await fileRequester.uploadVideo(
                formData,
                true, // compress 옵션
                (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            );

            if (response && response.uploads && response.uploads.length > 0) {
                const uploadInfo = response.uploads[0];
                setPreviewUrl(uploadInfo.url);

                // 비디오 ID가 있으면 처리 상태 추적 시작
                if (uploadInfo.videoId) {
                    setVideoId(uploadInfo.videoId);
                    setProcessingStatus('processing');
                    startStatusCheck(uploadInfo.videoId);
                }

                // 콜백 호출
                if (onUploadComplete) {
                    onUploadComplete(uploadInfo.url);
                }
            } else {
                throw new Error('업로드 응답이 유효하지 않습니다.');
            }
        } catch (error) {
            console.error('업로드 오류:', error);
            alert('파일 업로드 중 오류가 발생했습니다.');
        } finally {
            setIsUploading(false);
        }
    };

    // 상태 확인 함수 수정
    const startStatusCheck = (id) => {
        // 이전 인터벌 정리
        if (statusCheckInterval.current) {
            clearInterval(statusCheckInterval.current);
        }

        // 5초마다 상태 확인
        statusCheckInterval.current = setInterval(async () => {
            try {
                // fileRequester의 getVideoStatus 메소드 사용
                const statusData = await fileRequester.getVideoStatus(id);

                // statusData는 이미 ApiResponse에서 data 부분만 추출된 상태
                setProcessingStatus(statusData);

                // 처리 완료 또는 실패 시 인터벌 종료
                if (statusData === 'completed' || statusData.startsWith('failed')) {
                    clearInterval(statusCheckInterval.current);
                }
            } catch (error) {
                console.error('상태 확인 오류:', error);
            }
        }, 5000);
    };

    // 파일 제거 핸들러
    const handleRemove = () => {
        setFile(null);
        setFileName("선택된 파일 없음");
        setPreviewUrl(null);

        // 파일 입력 초기화
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    // 컴포넌트 언마운트 시 정리
    useEffect(() => {
        return () => {
            // 상태 확인 인터벌 정리
            if (statusCheckInterval.current) {
                clearInterval(statusCheckInterval.current);
            }

            // 미리보기 URL 해제
            if (previewUrl && previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(previewUrl);
            }
        };
    }, [previewUrl]);

    return (
        <div className="video-uploader">
            {/* 드래그 앤 드롭 영역 */}
            <div
                ref={dropAreaRef}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                style={{
                    width: '100%',
                    height: '200px',
                    border: '2px dashed #ccc',
                    borderRadius: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '20px',
                    marginBottom: '20px',
                    position: 'relative',
                    cursor: 'pointer',
                    backgroundColor: '#f9f9f9'
                }}
                onClick={() => fileInputRef.current.click()}
            >
                {previewUrl ? (
                    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                        {/* 비디오 미리보기 */}
                        {!previewUrl.endsWith('.m3u8') ? (
                            <video
                                src={previewUrl}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                                controls
                            />
                        ) : (
                            <HLSPlayer
                                src={previewUrl}
                                autoPlay={false}
                                controls={true}
                                width="100%"
                                height="100%"
                            />
                        )}

                        {/* 제거 버튼 */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRemove();
                            }}
                            style={{
                                position: 'absolute',
                                top: '10px',
                                right: '10px',
                                background: 'rgba(0,0,0,0.5)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                width: '30px',
                                height: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                zIndex: 10
                            }}
                        >
                            ✕
                        </button>
                    </div>
                ) : (
                    <>
                        <div style={{ fontSize: '48px', marginBottom: '10px' }}>🎬</div>
                        <p>비디오 파일을 여기에 끌어다 놓거나 클릭하여 선택하세요</p>
                        <p style={{ fontSize: '12px', color: '#666' }}>
                            지원 형식: MP4, MOV, AVI, MKV 등
                        </p>
                    </>
                )}
            </div>

            {/* 숨겨진 파일 입력 */}
            <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />

            {/* 파일 정보 및 업로드 버튼 */}
            {file && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '10px',
                    borderRadius: '4px',
                    backgroundColor: '#f0f0f0',
                    marginBottom: '20px'
                }}>
                    <div>
                        <p style={{ margin: 0 }}><strong>파일명:</strong> {fileName}</p>
                        <p style={{ margin: 0, fontSize: '12px' }}>
                            <strong>크기:</strong> {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </p>
                    </div>

                    <button
                        onClick={handleUpload}
                        disabled={isUploading}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: isUploading ? '#ccc' : '#4285f4',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: isUploading ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {isUploading ? '업로드 중...' : '업로드'}
                    </button>
                </div>
            )}

            {/* 업로드 진행 상태 */}
            {isUploading && (
                <div style={{ marginBottom: '20px' }}>
                    <div style={{
                        width: '100%',
                        backgroundColor: '#f0f0f0',
                        borderRadius: '4px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            height: '10px',
                            width: `${uploadProgress}%`,
                            backgroundColor: '#4285f4',
                            transition: 'width 0.3s'
                        }} />
                    </div>
                    <p style={{ textAlign: 'center', margin: '5px 0' }}>{uploadProgress}% 완료</p>
                </div>
            )}

            {/* 처리 상태 */}
            {processingStatus && (
                <div style={{
                    padding: '10px',
                    marginBottom: '20px',
                    borderRadius: '4px',
                    backgroundColor: processingStatus === 'completed' ? '#e6f7e6' : '#fff3e6',
                    color: processingStatus === 'completed' ? '#2e7d32' : '#ef6c00'
                }}>
                    <p style={{ margin: 0 }}>
                        <strong>처리 상태:</strong> {
                            processingStatus === 'completed' ? '변환 완료!' :
                                processingStatus === 'processing' ? '비디오 변환 중... 잠시만 기다려주세요.' :
                                    processingStatus
                        }
                    </p>
                </div>
            )}
        </div>
    );
};

export default VideoUploader;